import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import { Box, IconButton } from "@mui/material";
import dayjs from "dayjs";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import M3DatePicker from "../m3/M3DatePicker";

/**
 * AG Grid Custom Cell Editor with Material 3 DatePicker.
 * Supports selecting Month, Year, and Day interactively.
 * Commits standard YYYY-MM-DD strings to row data.
 */
const GridDateEditor = forwardRef((props, ref) => {
  const { value: initialValue, stopEditing, onValueChange, api } = props;

  // Normalize initial value
  const parseInitialDate = (val) => {
    if (!val || val === "N/A" || val === "None" || val === "-") return null;
    const cleanStr = String(val).replace(/\s+00:00:00.*$/, "").trim();
    const d = dayjs(cleanStr);
    return d.isValid() ? d : null;
  };

  const initialDayjs = parseInitialDate(initialValue);
  const initialFormatted = initialDayjs ? initialDayjs.format("YYYY-MM-DD") : "";

  const [dateValue, setDateValue] = useState(initialDayjs);
  const [open, setOpen] = useState(true);
  const valueRef = useRef(initialFormatted);
  const containerRef = useRef(null);

  useImperativeHandle(ref, () => ({
    getValue() {
      return valueRef.current;
    },
    afterGuiAttached: () => {
      // Ensure picker opens and input is focused
      setTimeout(() => {
        setOpen(true);
        const input = containerRef.current?.querySelector("input");
        input?.focus();
      }, 50);
    },
  }));

  const handleDateChange = (newDate, formattedStr) => {
    const finalVal = formattedStr || "";
    valueRef.current = finalVal;
    setDateValue(newDate);
    onValueChange?.(finalVal);

    // Stop editing after date is selected
    setOpen(false);
    setTimeout(() => {
      const stop = stopEditing || api?.stopEditing;
      if (stop) stop();
    }, 50);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    valueRef.current = "";
    setDateValue(null);
    onValueChange?.("");
    setOpen(false);
    const stop = stopEditing || api?.stopEditing;
    if (stop) stop();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.stopPropagation();
      setOpen(false);
      const stop = stopEditing || api?.stopEditing;
      if (stop) stop();
    } else if (e.key === "Escape") {
      e.stopPropagation();
      setOpen(false);
      const stop = stopEditing || api?.stopEditing;
      if (stop) stop(true); // cancel
    }
  };

  return (
    <Box
      ref={containerRef}
      onKeyDown={handleKeyDown}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        position: "relative",
        px: 0.5,
        backgroundColor: "transparent",
      }}
    >
      <M3DatePicker
        value={dateValue}
        onChange={handleDateChange}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        size="small"
        fullWidth
        autoFocus
        placeholder="YYYY-MM-DD"
        sx={{
          width: "100%",
          "& .MuiInputBase-root": {
            height: "32px",
            fontSize: "12.5px",
            paddingRight: dateValue ? "26px" : "4px",
            borderRadius: "6px",
            backgroundColor: "var(--m3-surface, #ffffff)",
          },
          "& input": {
            padding: "4px 8px !important",
            fontSize: "12px",
            textAlign: "center",
          },
        }}
      />
      {dateValue && (
        <IconButton
          size="small"
          onClick={handleClear}
          title="Clear Date"
          sx={{
            position: "absolute",
            right: 28,
            top: "50%",
            transform: "translateY(-50%)",
            padding: "2px",
            width: "18px",
            height: "18px",
            color: "var(--m3-outline, #94a3b8)",
            "&:hover": {
              color: "var(--m3-error, #ba1a1a)",
              backgroundColor: "transparent",
            },
          }}
        >
          <ClearRoundedIcon sx={{ fontSize: "13px" }} />
        </IconButton>
      )}
    </Box>
  );
});

export default GridDateEditor;
